<template>
  <div class="duck-container animate-fade-in" v-bind="$attrs">
    <div class="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
      <!-- Duck display section with enhanced effects -->
      <div class="w-full md:w-1/2 flex flex-col items-center slide-in-left-animation">
        <!-- Duck image container with enhanced animation -->
        <div class="duck-image-frame relative">
          
          <div class="relative z-10 w-full h-full flex items-center justify-center">
            <!-- Duck image with hover effects -->
            <img
              :src="duckImageUrl"
              @load="duckImageLoaded = true"
              alt="Rubber duck"
              class="duck-image max-w-full max-h-64 object-contain drop-shadow-xl transition-all duration-300"
            />
            
            <!-- Loading spinner -->
            <div v-if="!duckImageLoaded" class="absolute inset-0 flex items-center justify-center bg-white/30 backdrop-blur-sm rounded-full">
              <div class="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            <!-- Duck sparkle effects -->
            <span class="duck-sparkle"></span>
            <span class="duck-sparkle" style="--delay: 2s; --top: 40%; --left: 25%"></span>
            <span class="duck-sparkle" style="--delay: 3.5s; --top: 20%; --right: 20%; --size: 15px"></span>
          </div>
        </div>
      </div>
      
      <!-- Duck info section with glass morphism effect -->
      <div class="w-full md:w-1/2 p-6 bg-white/80  backdrop-blur-sm rounded-xl shadow-lg border border-white/20 mt-4 md:mt-0 slide-in-right-animation">
        <h3 class="text-xl font-bold text-slate-800  mb-5 flex items-center gap-2 pb-2 border-b border-gray-200 ">
          <div class="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full">
            <v-icon color="amber" size="18">mdi-duck</v-icon>
          </div>
          <span>Duck Details</span>
        </h3>
        
        <div class="space-y-6">
          <!-- Each info item with improved styling and explicit left-aligned text -->
          <div class="flex items-start gap-4 group">
            <div class="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center transition-colors group-hover:bg-yellow-200 ">
              <v-icon color="amber" size="20">mdi-ship-wheel</v-icon>
            </div>
            <div class="flex-grow text-left">
              <p class="text-sm text-gray-500 ">Cruise</p>
              <p class="font-medium text-slate-800 ">{{ cruiseName || 'Mystery Cruise' }}</p>
            </div>
          </div>
          
          <div class="flex items-start gap-4 group">
            <div class="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center transition-colors group-hover:bg-yellow-200 ">
              <v-icon color="amber" size="20">mdi-calendar-range</v-icon>
            </div>
            <div class="flex-grow text-left">
              <p class="text-sm text-gray-500 ">Dates</p>
              <div class="font-medium text-slate-800 ">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Start</span>
                  <span>{{ formattedStartDate }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">End</span>
                  <span>{{ formattedEndDate }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="mt-8 mx-auto max-w-2xl px-4">
      <div class="p-5 bg-gradient-to-r from-blue-50/90 to-blue-100/90 rounded-lg border-2 shadow-md">
        <div class="flex flex-col sm:flex-row items-center gap-4 mb-3">
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <!-- Keep Duck Option -->
          <div class="option-card relative p-4 rounded-lg bg-white/70 hover:bg-white border border-green-200 transition-all shadow-sm hover:shadow-md">
            <div class="flex flex-col items-center mb-3">
              <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <v-icon size="20" color="green">mdi-duck</v-icon>
              </div>
              <h4 class="font-bold text-gray-800 mb-1">Keep Duck</h4>
            </div>
            <p class="text-sm text-center text-gray-600 leading-snug">
              Take this duck home as a special souvenir from your cruise journey!
            </p>
          </div>

          <!-- Hide Duck Option -->
          <div class="option-card relative p-4 rounded-lg bg-white/70  hover:bg-white border border-yellow-200 transition-all shadow-sm hover:shadow-md">
            <div class="flex flex-col items-center mb-3">
              <div class="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mb-2">
                <v-icon size="20" color="amber">mdi-map-marker-plus</v-icon>
              </div>
              <h4 class="font-bold text-gray-800 mb-1">Hide Again</h4>
            </div>
            <p class="text-sm text-center text-gray-600 leading-snug">
              Hide it again somewhere on the ship for another passenger to discover!
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Action buttons with enhanced animation and clearer choices -->
    <div class="mt-6 flex flex-col sm:flex-row justify-center w-full gap-3">
      <button 
        @click="hideDuck" 
        class="px-4 sm:px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg shadow-md transition-all hover:shadow-lg hover:translate-y-[-2px] flex items-center gap-2 min-w-[120px] justify-center"
      >
        <v-icon size="20" color="white" class="transition-transform duration-300">mdi-map-marker-plus</v-icon>
        <span class="text-base font-medium">Hide Again</span>
      </button>
    </div>
  </div>
  
  <!-- Confirmation Dialog for Hiding Duck -->
  <v-dialog
    v-model="showHideConfirmation"
    max-width="500"
    persistent
    scroll-strategy="block"
    retain-focus
  >
    <v-card class="bg-white  rounded-xl">
      <v-card-title class="text-slate-800  pb-2">
        <div class="flex items-center gap-2 pb-2">
          <v-icon color="amber" size="24">mdi-map-marker-alert</v-icon>
          <span>Hide Duck Guidelines</span>
        </div>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="pt-4">
        <p class="text-gray-700  mb-4">
          You're about to hide this duck for someone else to find. Please follow these guidelines:
        </p>
        
        <div class="bg-yellow-50/80  rounded-lg p-4 border border-yellow-100 mb-4">
          <h4 class="font-bold text-gray-800  flex items-center gap-2 mb-2">
            <v-icon color="amber" size="20">mdi-alert-circle</v-icon>
            Don't hide ducks in these locations:
          </h4>
          <ul class="list-disc ml-5 text-gray-700  space-y-1">
            <li>By pool decks or near water features</li>
            <li>Inside boutiques or retail shops</li>
            <li>In dining areas or restaurants</li>
            <li>Near safety equipment or exits</li>
            <li>Areas marked as off-limits to passengers</li>
          </ul>
        </div>
        
        <div class="bg-green-50/80  rounded-lg p-4 border border-green-100 ">
          <h4 class="font-bold text-gray-800  flex items-center gap-2 mb-2">
            <v-icon color="green" size="20">mdi-check-circle</v-icon>
            Good hiding spots include:
          </h4>
          <ul class="list-disc ml-5 text-gray-700  space-y-1">
            <li>Public lounges or sitting areas</li>
            <li>Near artwork or ship displays</li>
            <li>In library or game room areas</li>
            <li>Public hallways (not blocking paths)</li>
            <li>Near plants or decorative features in public areas</li>
          </ul>
        </div>
      </v-card-text>
      
      <v-card-actions class="px-4 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          variant="tonal"
          color="gray"
          @click="showHideConfirmation = false"
          class="mr-2"
        >
          Cancel
        </v-btn>
        <v-btn
          color="warning"
          variant="elevated"
          @click="confirmHideDuck"
          :loading="isHiding"
        >
          <v-icon size="20" class="mr-1">mdi-duck</v-icon>
          Hide Duck
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  
  <!-- Success Dialog after hiding duck -->
  <v-dialog
    v-model="showSuccessMessage"
    max-width="400"
    persistent
    scroll-strategy="block"
    retain-focus
  >
    <v-card class="bg-white  rounded-xl">
      <v-card-title class="text-slate-800  pb-2 pt-4">
        <div class="flex items-center gap-2 justify-center w-full">
          <v-icon color="success" size="24">mdi-check-circle</v-icon>
          <span>Duck Hidden Successfully!</span>
        </div>
      </v-card-title>
      
      <v-card-text class="pt-4 text-center">
        <p class="text-gray-700  mb-4">
          Thank you for sharing the joy with fellow cruisers!
        </p>
        <div class="w-20 h-20 mx-auto mb-4">
          <v-icon size="80" color="yellow">mdi-duck</v-icon>
        </div>
        <p class="text-gray-600  text-sm">
          Redirecting to homepage...
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDemoCruiseStore } from '@/stores/demoCruiseStore';
import { useDemoDuckStore } from '@/stores/demoDuckStore';

dayjs.extend(utc);
const duckStore = useDemoDuckStore();
const cruiseStore = useDemoCruiseStore();
const router = useRouter();

const { currentDuck: duck } = storeToRefs(duckStore);

const duckImageLoaded = ref(false);
const showHideConfirmation = ref(false);
const showSuccessMessage = ref(false);
const isHiding = ref(false);

// Duck cruise info
const cruiseName = computed(() => {
  if (!duck.value?.idCruise) return 'Mystery Cruise';
  const cruise = cruiseStore.getCruiseById(duck.value.idCruise);
  return cruise?.name || 'Mystery Cruise';
});

// Format cruise dates - Split to start and end date separately
const formattedStartDate = computed(() => {
  if (!duck.value?.idCruise) return 'Start date unknown';
  const cruise = cruiseStore.getCruiseById(duck.value.idCruise);
  if (!cruise?.startDate) return 'Start date unknown';
  
  // Use UTC to preserve the exact date
  return dayjs.utc(cruise.startDate).format("MMMM DD, YYYY");
});

const formattedEndDate = computed(() => {
  if (!duck.value?.idCruise) return 'End date unknown';
  const cruise = cruiseStore.getCruiseById(duck.value.idCruise);
  if (!cruise?.endDate) return 'End date unknown';
  
  // Use UTC to preserve the exact date
  return dayjs.utc(cruise.endDate).format("MMMM DD, YYYY");
});

// Default duck image
const DEFAULT_DUCK_ICON = "/ducksImages/duckIcon.webp";

// Handle duck image - now supports rarity-based images
const duckImageUrl = computed(() => {
  if (!duck.value) return DEFAULT_DUCK_ICON;
  
  // If duck has rarity, use rarity-based image
  if (duck.value.rarity) {
    return `/duckRarity/${duck.value.rarity}.webp`;
  }
  
  // Fallback to individual duck image
  if (duck.value.image) {
    // Handle base64 image data
    if (duck.value.image.startsWith('data:')) {
      return duck.value.image;
    }
    
    // Handle relative paths
    if (duck.value.image.startsWith('/')) {
      return duck.value.image;
    }
    
    // Handle base64 string without prefix
    try {
      return `data:image/webp;base64,${duck.value.image}`;
    } catch (e) {
      console.error('Error setting duck image:', e);
      return DEFAULT_DUCK_ICON;
    }
  }
  
  return DEFAULT_DUCK_ICON;
});

// Handle hide duck action
const hideDuck = () => {
  showHideConfirmation.value = true;
};

// Confirm hiding duck and redirect to homepage
const confirmHideDuck = async () => {
  if (duck.value?.uuid) {
    isHiding.value = true;
    try {
      await duckStore.hideDuck(duck.value.uuid);
      showHideConfirmation.value = false;
      showSuccessMessage.value = true;
      
      // Small delay before redirect for better UX
      setTimeout(() => {
        showSuccessMessage.value = false;
        router.push('/demo');  
      }, 2000);
    } catch (error) {
      console.error('Error hiding duck:', error);
    } finally {
      isHiding.value = false;
    }
  }
};

</script>

<style scoped>
.duck-container {
  width: 100%;
  transition: all 0.3s ease;
}

.duck-image-frame {
  position: relative;
  width: 100%;
  max-width: 260px; /* Reduced from 300px for better small screen fit */
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.duck-image {
  z-index: 10;
  filter: drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1));
  transition: all 0.5s ease;
}

.duck-image:hover {
  transform: scale(1.05) rotate(5deg);
  filter: drop-shadow(0 20px 13px rgb(0 0 0 / 0.03)) drop-shadow(0 8px 5px rgb(0 0 0 / 0.08));
}

/* Add a subtle floating animation to the duck */
.duck-image {
  animation: gentle-float 5s ease-in-out infinite alternate;
}

@keyframes gentle-float {
  0% { transform: translateY(0) rotate(-2deg); }
  100% { transform: translateY(-10px) rotate(2deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .duck-image-frame {
    margin: 0 auto;
    max-width: 220px; /* Even smaller on mobile devices */
  }
  
  .duck-image {
    max-height: 180px; /* Control max height on small screens */
  }
}

@media (max-width: 400px) {
  .duck-image-frame {
    max-width: 180px; /* Smaller for very small screens */
  }
}

/* Improved touch targets for mobile */
button {
  min-height: 48px; /* Minimum height for good touch targets */
  min-width: 120px; /* Ensure reasonable width for touch */
  touch-action: manipulation; /* Disable double-tap to zoom */
}

/* New animations */
@keyframes pulse-slow {
  0%, 100% { opacity: 0.6; transform: scale(0.98); }
  50% { opacity: 0.8; transform: scale(1); }
}

.animate-pulse-slow {
  animation: pulse-slow 4s infinite alternate;
}

.slide-in-left-animation {
  opacity: 0;
  transform: translateX(-20px);
  animation: slideInLeft 0.8s forwards;
}

.slide-in-right-animation {
  opacity: 0;
  transform: translateX(20px);
  animation: slideInRight 0.8s forwards;
  animation-delay: 0.2s;
}

@keyframes slideInLeft {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fade-in {
  opacity: 0;
  animation: fadeIn 0.5s forwards;
}

@keyframes fadeIn {
  to { opacity: 1; }
}

/* Duck sparkle animation */
.duck-sparkle {
  position: absolute;
  width: var(--size, 20px);
  height: var(--size, 20px);
  background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%);
  border-radius: 50%;
  opacity: 0;
  top: var(--top, 30%);
  left: var(--left, auto);
  right: var(--right, 25%);
  animation: sparkle 4s infinite;
  animation-delay: var(--delay, 0s);
  z-index: 12;
  pointer-events: none;
}

@keyframes sparkle {
  0%, 100% { opacity: 0; transform: scale(0.2); }
  50% { opacity: 1; transform: scale(1); }
}

/* Water ripple effect */
.ripple-effect {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: var(--size, 100%);
  height: var(--size, 100%);
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: ripple 8s infinite;
  animation-delay: var(--delay, 0s);
  opacity: 0;
}

@keyframes ripple {
  0% { width: 0%; height: 0%; opacity: 0.8; }
  100% { width: 100%; height: 100%; opacity: 0; }
}

/* Handling reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .duck-image,
  .animate-pulse-slow,
  .slide-in-left-animation,
  .slide-in-right-animation,
  .animate-fade-in,
  .duck-sparkle,
  .ripple-effect {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}

/* Add styles for the enhanced options display */
.animate-pulse-slow {
  animation: gentle-pulse 3s ease-in-out infinite;
}

@keyframes gentle-pulse {
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.2);
    transform: scale(1);
  }
  50% { 
    box-shadow: 0 0 10px 0 rgba(59, 130, 246, 0.3);
    transform: scale(1.01);
  }
}

.option-card {
  transform: translateY(0);
  transition: all 0.2s ease;
}

.option-card:hover {
  transform: translateY(-3px);
}

/* Pulsating dot animation */
.pulse-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #3b82f6; /* blue-500 */
  margin-left: 5px;
  animation: pulse-dot 1.5s ease infinite;
}

@keyframes pulse-dot {
  0% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0.5; }
}

/* Media query for reduced motion */
@media (prefers-reduced-motion: reduce) {
  .animate-pulse-slow,
  .option-card:hover,
  .pulse-dot {
    animation: none !important;
    transform: none !important;
    transition: none !important;
  }
}
</style>